import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react'
import { MenuTrigger } from 'react-aria-components'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { ObjectProperties, SavedView } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { ObjectTypes } from '../api/object'
import { useSavedViewListAll } from '../api/saved-views'
import { Button } from '../components/button'
import { Menu, MenuItem, Popover } from '../components/menu'
import { ObjectPropertyFilter } from '../components/object-property-filter'
import { CreateViewModal, UpdateViewModal } from '../components/saved-view-modals'
import { SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { useFormLayout } from '../layouts'
import { isLayoutElementComponent } from '../layouts/types'
import { quickDeepEqual } from '../query-deep-equal'

export function FilteringSidepanel<T extends ObjectProperties = ObjectProperties>({
	object,
	viewId,
	filter: initialFilter,
	onClose,
	onSubmit,
	renderExtra,
}: {
	object: keyof ObjectTypes
	viewId?: string
	filter: T
	onClose: VoidFunction
	onSubmit: (filter: T) => void
	renderExtra?: (args: { filter: T; setFilter: Dispatch<SetStateAction<T>> }) => ReactNode
}) {
	const { layout = [], properties = [] } = useFormLayout(object, 'filter')
	const objectProperties = useMemo(() => {
		const elements = layout.filter(isLayoutElementComponent)
		if (elements.length) return elements
		return properties
	}, [layout, properties])
	const [filter, setFilter] = useState<T>(initialFilter ?? {})
	const [savingModal, setSavingModal] = useState<null | 'new' | 'update'>(null)
	const { data: savedViews = [] } = useSavedViewListAll(object)
	const view = useMemo(
		() => savedViews.find(savedView => savedView.id === viewId),
		[savedViews, filter],
	)
	const isSelected = !quickDeepEqual(view?.filter ?? {}, filter)
	const isUnchanged = quickDeepEqual(initialFilter, filter)

	const canCreateViews = useHasAccountPermission(ACCOUNT_PERMISSION.ADD_DASHBOARD_VIEWS)
	const canEditViews = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_DASHBOARD_VIEWS)

	const handleSubmit = () => {
		onClose()
		onSubmit(filter)
	}

	return (
		<>
			<SidepanelContent className="space-y-4">
				{objectProperties.map(({ identifier, ...property }) => (
					<ObjectPropertyFilter
						key={identifier}
						{...property}
						value={filter[identifier]}
						onChange={value => {
							setFilter(filter => ({
								...filter,
								[identifier]: value,
							}))
						}}
					/>
				))}
				{renderExtra?.({ filter, setFilter })}
			</SidepanelContent>
			<SidepanelFooter>
				<div className="flex justify-between items-center gap-2 w-full">
					<Button variant="PRIMARY" isDisabled={isUnchanged} onPress={handleSubmit}>
						Filtrovat
					</Button>
					{isSelected ? (
						<>
							{view && (canCreateViews || canEditViews) ? (
								<MenuTrigger>
									<Button variant="SECONDARY">Uložit ...</Button>
									<Popover>
										<Menu>
											{canCreateViews ? (
												<MenuItem onAction={() => setSavingModal('new')}>
													Jako nový pohled
												</MenuItem>
											) : null}
											{canEditViews ? (
												<MenuItem onAction={() => setSavingModal('update')}>
													Změny pro existující
												</MenuItem>
											) : null}
										</Menu>
									</Popover>
								</MenuTrigger>
							) : canCreateViews ? (
								<Button variant="SECONDARY" onPress={() => setSavingModal('new')}>
									Uložit
								</Button>
							) : null}
							<Button
								variant="TEXT"
								size="TEXT"
								className="ml-auto"
								onPress={() => {
									onClose()
									onSubmit({} as T)
								}}
							>
								Zrušit
							</Button>
						</>
					) : null}
				</div>
			</SidepanelFooter>
			{savingModal === 'update' && (
				<UpdateViewModal
					onClose={() => setSavingModal(null)}
					object={object}
					view={view as SavedView}
					filter={filter}
					onSubmit={handleSubmit}
				/>
			)}
			{savingModal === 'new' && (
				<CreateViewModal
					object={object}
					onClose={() => setSavingModal(null)}
					filter={filter}
					onSubmit={handleSubmit}
				/>
			)}
		</>
	)
}
